// angular
import { NgClass, NgOptimizedImage } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { InputComponent } from '@app/core/components/input/input.component';
import { UploadFilesComponent } from '@app/core/components/upload-files/upload-files.component';
import { SelectDropdownComponent } from '@app/core/components/select-dropdown/select-dropdown.component';
import { RegisterButtonComponent } from '../register-button/register-button.component';
// services
import { QuickOrderService } from '@app/+quick-order/shared/services/quick-order.service';
import { CategoriesService } from '@app/+categories/shared/services/categories.service';
import { UserService } from '@app/shared/services/user.service';
import { RecaptchaService } from '@app/shared/services/recaptcha.service';
// interfaces & enums
import { IUploadFile } from '@app/core/shared/interfaces/upload-file.interface';
import { I18n } from '@app/shared/enums/i18n.enum';
// utils
import { getAvailableTranslation } from '@app/shared/utils/get-available-translation';
import { RecaptchaData } from '@app/shared/types/recaptcha-data.type';
import { environment } from '@environments/environment';
import { MatomoTrackClickDirective } from 'ngx-matomo-client';
import { EmailValidator } from '@app/shared/validators/email.validator';

@Component({
  standalone: true,
  selector: 'pwj-order-quick-access',
  imports: [
    NgOptimizedImage,
    ReactiveFormsModule,
    FormsModule,
    InputComponent,
    ButtonComponent,
    UploadFilesComponent,
    TranslateModule,
    SelectDropdownComponent,
    NgClass,
    RegisterButtonComponent,
    MatomoTrackClickDirective,
  ],
  templateUrl: './order-quick-access.component.html',
  styleUrl: './order-quick-access.component.scss',
})
export class OrderQuickAccessComponent {
  public isShowingBox: boolean = false;

  // form property
  public orderForm!: FormGroup;
  public formIsSubmitting: boolean = false;
  // current lang
  public currentLang!: string;

  // product categories
  public productCategories: { value: string; label: string }[] = [];

  public isUserLoggedIn!: boolean;
  public isDisabledSubmitBtn: boolean = false;
  public isError: boolean = false;

  // dependencies
  private readonly _translateService = inject(TranslateService);
  private readonly _quickOrderService = inject(QuickOrderService);
  private readonly _categoriesService = inject(CategoriesService);
  private readonly _fb = inject(FormBuilder);
  private readonly _userService = inject(UserService);
  private readonly _recaptchaService = inject(RecaptchaService);

  constructor() {
    // set current lang
    this.currentLang = this._translateService.currentLang;
    // set lang on change
    this._translateService.onLangChange.subscribe(data => {
      this.currentLang = data.lang;
    });
    // checked user logged-in or no
    effect(() => {
      this.isUserLoggedIn = !!this._userService.userSignal()?.username;
    });
    // initialize order form
    this.orderForm = this._fb.group({
      title: ['', Validators.required],
      category_id: [null],
      email: [null],
      description: ['', Validators.required],
      file_ids: [null],
    });

    effect(() => {
      if (this._recaptchaService.recaptchaData() !== null) {
        const captchaData =
          this._recaptchaService.recaptchaData() as RecaptchaData;

        if (captchaData?.score < environment.recaptcha.targetScore) {
          this.isDisabledSubmitBtn = true;
        }
      }
    });

    this.orderForm.valueChanges.subscribe(_ => {
      if (!this.orderForm.dirty) {
        this.isDisabledSubmitBtn = true;
      }
    });

    effect(() => {
      if (this._quickOrderService.orderSubmitState()) {
        // set form is submitting
        this.formIsSubmitting = false;
        this.orderForm.reset();
        this._quickOrderService.orderSubmitState.set(false);
      }
    });

    // close dialog on order submitted
    effect(() => {
      if (this._quickOrderService.orderSubmitState()) {
        // set form is submitting
        this.formIsSubmitting = false;
        this.orderForm.reset();
        if (this.isShowingBox) {
          this.isShowingBox = false;
          this._quickOrderService.orderSubmitState.set(false);
        }
      }
    });

    effect(() => {
      this.isError = this._quickOrderService.orderSubmitFailed();
    });

    effect(() => {
      this.setCategories();
    });
  }

  public submitOrder() {
    // validation
    this.orderForm.markAllAsTouched();
    if (this.orderForm.invalid || !this.orderForm.dirty) {
      return;
    }

    // set form is submitting
    this.formIsSubmitting = true;
    // implement order form
    const { email, file_ids, ...rest } = this.orderForm.value;

    const order = {
      ...rest,
      file_ids: file_ids?.map((file: IUploadFile) => file._id) || [],
      email: !email || email?.length < 2 ? null : email,
    };

    if (order.category_id === null || order.category_id?.length === 0) {
      delete order.category_id;
    }

    // submit quick order form
    this._quickOrderService.postQuickOrder(order);
  }

  public setCategories() {
    // set product categories
    this.productCategories = this._categoriesService
      .categoriesData()
      ?.map(categoryType => {
        return {
          value: categoryType._id,
          label: getAvailableTranslation(
            categoryType?.title,
            this.currentLang as I18n
          ),
        };
      });
  }

  public toggleOrderForm() {
    this.isShowingBox = !this.isShowingBox;

    if (this.isShowingBox) {
      this._recaptchaService.recaptchaValidation('quick_order_form');
    }
  }
}
